import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CustomDialogTitle } from '../Common/Custom/CustomDialogTitle';
import {
    BEEScorecardUserAccess
} from "../../Component/Model/Dto/Scorecard/BEEScorecardUserAccessDto";
import { BEEScorecardUserAccessService } from "../../Component/Api/BBEScorecardUserAccess/BEEScorecardUserAccessService";
import { toast } from 'react-toastify';
import AppMultiSelect from "../../Component/Common/Components/FormValidationInputs/AppMultiSelect";
import { EntriesPerPage } from "../../Component/Model/Table/Table";
import { DataTable } from "../../Component/Common/Components/DataTable/DataTable";
import { LinkedUserTableMapper } from "./LinkedUsersColumns";
import { BEE123UserTypes, usersList } from "../../Component/Common/Constants/Lists/Users";
import { useRecoilValue } from "recoil";
import { UserAtom } from "../../State/user.Atom";
import { IDropDownOptions } from "../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { ColumnSortOption } from "../../Component/Model/Dto/ColumnSortOption";
import { OrganisationService } from "../../Component/Api/Organisation/OrganisationService";
import { OrganisationAccess } from "../../Component/Model/Dto/Organisation/OrganisationUserAccessDto";
import { MDButton } from "../../Component/Common/Components/MDButton";
import AutocompleteWithInfo from "../../Component/Common/Components/Autocomplete/AutocompleteWithInfo";

interface IProps {
    id: string | number;
    showAssignModal: boolean;
    isOrganisationUserAccess: boolean;
    isEEScorecard?: boolean;
    closeAssignModal: () => void;
}

export const AssignModal = (props: IProps) => {
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const currentUser = useRecoilValue(UserAtom);

    const { control, reset, watch, handleSubmit, formState: { isSubmitting }, setValue } = useForm({
        mode: 'all',
    });

    const [availableUsers, setAvailableUsers] = useState<BEEScorecardUserAccess.linkedUsers[]>([]);
    const [linkedUsers, setLinkedUsers] = useState<BEEScorecardUserAccess.linkedUsers[]>([]);
    const [addUsers, setAddUsers] = useState<BEEScorecardUserAccess.linkedUsers[]>([]);
    const [removeUsers, setRemoveUsers] = useState<BEEScorecardUserAccess.linkedUsers[]>([]);
    const [scorecardName, setScorecardName] = useState("");
    const [userLimitReached, setUserLimitReached] = useState(true);
    const [linkedUserCount, setLinkedUserCount] = useState<number>(0);
    const selectedUser = watch('findUserList');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
    const [isUserAvailable, setIsUserAvailable] = useState<boolean>(true);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>('');
    const [filteredUsers, setFilteredUsers] = useState<BEEScorecardUserAccess.linkedUsers[]>([]);
    const [userTypes, setUserTypes] = useState<IDropDownOptions[]>([]);
    const selectedUserTypes = watch('filteredUserTypes');
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    }

    const getAssignAccessData = async (id: string | number) => {
        if (id === undefined) return;
        try {
            if (props.isOrganisationUserAccess) {
                setIsLoading(true);
                const result = await OrganisationService.getUserAccessById(+id);
                setScorecardName(result.data.registrationName);
                setLinkedUserCount(result.data.linkedUserCount);
                setAvailableUsers(result.data.availableUsers);
                setUserLimitReached(false);
                setIsUserAvailable(result.data.availableUsers.length > 0);
            } else {
                setIsLoading(true);
                const result = await BEEScorecardUserAccessService.getAvailableScorecardUserAccessById(id);
                reset(result.data as BEEScorecardUserAccess.dto);
                setScorecardName(result.data.scorecardName)
                setAvailableUsers(result.data.availableUsers)
                setLinkedUserCount(result.data.linkedUserCount);
                setUserLimitReached(result.data.linkedUserCount >= result.data.userLimit);
            }

        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const linkUserSearchRequest: BEEScorecardUserAccess.SearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        columnSortOptions: columnSortOptions,
        searchValue: searchValue,
        scorecardId: props.id
    };

    const OrganisationlinkUserSearchRequest: OrganisationAccess.OrganisationSearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        columnSortOptions: columnSortOptions,
        searchValue: searchValue,
        organisationId: props.id
    };

    useEffect(() => {
        fetchLinkedUserData(true);
    }, [entriesPerPage, pageNumber, columnSortOptions, searchValue]);

    const fetchLinkedUserData = async (filterUsers: boolean) => {
        setIsTableLoading(true);
        try {
            let linkedUserResult;

            if (props.isOrganisationUserAccess) {
                linkedUserResult = await OrganisationService.searchOganisationUserAccess(OrganisationlinkUserSearchRequest);
            } else {
                linkedUserResult = await BEEScorecardUserAccessService.searchLinkedScorecardUserAccess(linkUserSearchRequest);
            }

            const addedUserIds = new Set(addUsers.map(user => user.userId));
            const removeUserIds = new Set(removeUsers.map(user => user.userId));

            let filtered = linkedUserResult.data.results;

            if (filterUsers) {
                filtered = filtered.filter(user => !removeUserIds.has(user.userId) && !addedUserIds.has(user.userId));
            }

            if (pageNumber === 1) {
                setLinkedUsers(filterUsers ? [...addUsers, ...filtered] : filtered);
            } else {
                setLinkedUsers(filtered);
            }

            setFilteredItemsCount(linkedUserResult.data.filteredItemsCount);
            setTotalItemsCount(linkedUserResult.data.totalItemsCount);

        } catch (error: any) {
            setLinkedUsers([]);
            setFilteredItemsCount(0);
            setTotalItemsCount(0);
            toast.error(error.response.data);
        } finally {
            setIsTableLoading(false);
        }
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const getUserTypesList = () => {
        let filteredUsersList = usersList;

        if (props.isEEScorecard === true) {
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'VA User');
        }

        if (currentUser.hasAuditRole) {
            const userTypeMap: { [key: string]: string } = {
                'Sub-Admin': 'Technical Signatory',
                'User': 'Analyst',
            };

            filteredUsersList = filteredUsersList.filter(item =>
                ['User', 'Sub-Admin', 'Client', 'Client Admin', 'Internal Auditor', 'Scorecard Admin', 'VA User'].includes(item.value)
            ).map(item => ({
                ...item,
                value: userTypeMap[item.value] || item.value,
            }));

            if (currentUser.isScorecardAdmin || currentUser.isInternalAuditor) {
                filteredUsersList = filteredUsersList.filter(item => item.value !== 'Scorecard Admin');
            } else if (currentUser.isSubAdmin) {
                filteredUsersList = filteredUsersList.filter(item => item.value !== 'Technical Signatory');
            }
        } else if (currentUser.isAdmin) {
            filteredUsersList = filteredUsersList.filter(item => ['User', 'Sub-Admin', 'VA User'].includes(item.value));
        } else if (currentUser.isSubAdmin) {
            filteredUsersList = filteredUsersList.filter(item => ['User', 'VA User'].includes(item.value));
        } else if (currentUser.isClientAdmin) {
            filteredUsersList = filteredUsersList.filter(item => item.value === 'Client');
        }

        filteredUsersList.sort((a, b) => a.value.localeCompare(b.value));

        setUserTypes(filteredUsersList);
    }

    const handleDeleteLinkedUser = (index: number) => {
        const removedUser = linkedUsers[index];

        const updatedAddUsers = addUsers.filter((_, i) => i !== index);
        const updatedLinkedUsers = linkedUsers.filter((_, i) => i !== index);

        setAddUsers(updatedAddUsers);
        setLinkedUsers(updatedLinkedUsers);

        setAvailableUsers((prevAvailableUsers) => {
            const userWasInLinkedUsers = linkedUsers.includes(removedUser);
            const updatedAvailableUsers = userWasInLinkedUsers ? [...prevAvailableUsers, removedUser] : prevAvailableUsers;
            return updatedAvailableUsers
        });

        setRemoveUsers((prevRemoveUsers) => {
            const userWasInLinkedUsers = linkedUsers.includes(removedUser);
            return userWasInLinkedUsers ? [removedUser, ...prevRemoveUsers] : prevRemoveUsers;
        });
        setUserLimitReached(false);
        setIsUserAvailable(true);
        setValue('findUserList', undefined);
    };

    interface userItem {
        id: string,
        value: string,
        description: string
    }

    const handleAddLinkedUser = (event: userItem) => {
        setValue('findUserList', undefined);
        const selectedUserObject = availableUsers.find(user => user.userId === event.id);
        if (selectedUserObject) {

            selectedUserObject.isNewLinkedUser = true;
            setLinkedUsers([selectedUserObject, ...linkedUsers]);
            setAddUsers([selectedUserObject, ...addUsers]);
            setLinkedUserCount(linkedUserCount + 1);
            setAvailableUsers(availableUsers.filter(user => user.userId !== selectedUserObject.userId));

            const updatedRemoveUsers = removeUsers.filter(user => user.userId !== selectedUserObject.userId);
            setRemoveUsers(updatedRemoveUsers);
        }
    }

    const handleDataSubmit = async (data: FieldValues) => {
        setIsSaving(true);
        try {
            if (props.isOrganisationUserAccess) {
                const finalData = {
                    ...data,
                    organisationId: props.id,
                    addUsers: addUsers,
                    removeUsers: removeUsers
                } as OrganisationAccess.SaveDto;
                await OrganisationService.updateUserAccess(finalData);
                toast.success("Changes saved successfully")

                setAddUsers([]);
                setRemoveUsers([]);
            } else {
                const finalData = {
                    ...data,
                    addUsers: addUsers,
                    removeUsers: removeUsers
                } as BEEScorecardUserAccess.SaveDto;
                await BEEScorecardUserAccessService.saveScorecardUserAccess(finalData);

                setAddUsers([]);
                setRemoveUsers([]);
            }

        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
            fetchLinkedUserData(false);
            getAssignAccessData(props.id);
        }
    }

    useEffect(() => {
        const filteredUsers = availableUsers.filter(user => {
            if (selectedUserTypes?.length > 0) {
                return selectedUserTypes.includes(user.userType);
            }
            else {
                return userTypes.some(i => i.id === user.userType);
            }
        }).filter(f => !addUsers.includes(f));
        setFilteredUsers(filteredUsers);
    }, [selectedUserTypes, addUsers, availableUsers])

    useEffect(() => {
        getUserTypesList();
        getAssignAccessData(props.id);
    }, [props.showAssignModal])

    useEffect(() => {
        if (userTypes.length > 0) {
            const defaultSelection = currentUser.isClientAdmin ? [BEE123UserTypes.Client] : [BEE123UserTypes.SubAdmin, BEE123UserTypes.User];
            setValue("filteredUserTypes", defaultSelection);
        }
    }, [userTypes]);

    const tableData = LinkedUserTableMapper(
        linkedUsers,
        handleDeleteLinkedUser
    );

    return (
        <Dialog disableEnforceFocus open={props.showAssignModal} onClose={() => { props.closeAssignModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
            <CustomDialogTitle id="assign-dialog-title" onClose={() => props.closeAssignModal()}>
                {props.isOrganisationUserAccess ? `Edit User Access for Organisation - ` + scorecardName : `Edit User Access for Scorecard - ` + scorecardName}
            </CustomDialogTitle>
            <DialogContent sx={{ height: '500px', width: '950px' }}>
                {isLoading && isTableLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <form id='IndividualForm'>
                        <Grid container paddingLeft={2} paddingBottom='0px'>
                            <Grid item xs={12} paddingBottom='0px'>
                                <FormControl fullWidth sx={{ paddingBottom: '0px', width: '99%' }}>
                                    <Box mt={2}>
                                        <AppMultiSelect control={control} name="filteredUserTypes" label="Filter Users" options={userTypes} />
                                    </Box>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid container paddingLeft={2} paddingTop={2} paddingBottom='0px' direction="column">


                            <Grid item xs={12} paddingBottom='0px' key={linkedUserCount}>
                                <FormControl fullWidth sx={{ paddingBottom: '0px' }}>
                                    {filteredUsers.length > 0 && !userLimitReached ?
                                        <>
                                            <AutocompleteWithInfo
                                                control={control}
                                                name="findUserList"
                                                label="Find User"
                                                items={filteredUsers.map(user => ({
                                                    id: user.userId,
                                                    value: user.name,
                                                    description: `${user.email} | ${user.userTypeDescription}`
                                                }))}
                                                size={"small"}
                                                onChange={handleAddLinkedUser}
                                                style={{ width: '99%' }}
                                            />
                                        </>
                                        :
                                        <>
                                            {
                                                props.isOrganisationUserAccess ? (
                                                    <Alert severity="warning" style={{ height: 50, padding: 1, maxHeight: 200, overflowY: 'auto', width: '99%' }}>All users are already assigned to this organisation.</Alert>
                                                ) : userLimitReached ? (
                                                    <Alert severity="warning" style={{ height: 50, padding: 1, maxHeight: 200, overflowY: 'auto', width: '99%' }}>User limit reached.</Alert>
                                                ) : (
                                                    <Alert severity="info" style={{ height: 50, padding: 1, maxHeight: 200, overflowY: 'auto', width: '99%' }}>No users of this type to assign.</Alert>
                                                )}
                                        </>
                                    }
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid container padding={2}>
                            <Grid item xs={12} width='99%'>
                                <DataTable
                                    table={tableData}
                                    entriesPerPage={entriesPerPage}
                                    pageNumber={pageNumber}
                                    filteredItemsCount={filteredItemsCount}
                                    totalItemsCount={totalItemsCount}
                                    entriesPerPageChanged={entriesPerPageChanged}
                                    pageNumberChanged={pageNumberChanged}
                                    isLoading={isTableLoading}
                                    manualSorting={true}
                                    columnSortingChanged={columnSortingChanged}
                                    canSearch={true}
                                    searchValueChanged={searchValueChanged}
                                ></DataTable>

                            </Grid>
                        </Grid>
                    </form>
                )}
            </DialogContent>
            <DialogActions>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flexGrow: 1 }} />
                    <LoadingButton
                        loading={isSubmitting}
                        variant='contained'
                        form='AssignForm'
                        type="submit"
                        color='primary'
                        disabled={isSaving || !isUserAvailable}
                        onClick={() => {
                            handleSubmit(handleDataSubmit)()
                                .then(() => {

                                })
                                .catch(() => {
                                    toast.error("An error occured while saving")
                                });
                        }}>
                        Save
                    </LoadingButton>
                    <MDButton
                        variant='outlined'
                        form='AssignForm'
                        color='primary'
                        disabled={isSaving}
                        onClick={() => props.closeAssignModal()}
                        style={{ marginLeft: '8px' }}>
                        Cancel
                    </MDButton>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
